import { Injectable } from '@angular/core';

import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';

import { finalize, tap } from 'rxjs/operators';

import {
  AllTagsPresenterModel,
  DataQueryPresenterModel,
  ManagedHttpContext,
  TagGroupPresenterModel,
} from '@osos/shared/types';
import { of } from 'rxjs';

import { TagsControllerService } from '@osos/shared/api';
import { QueryFactoryService } from '../../factories';
import { DataQueryService } from '../../services';
import { LoadingActions } from '../loading/loading.actions';
import { CoreQueryActions } from './core-query.action';

export interface CoreQueryStateModel {
  presentedQuery: { [key: string]: DataQueryPresenterModel };
  presentedTags: { [key: string]: AllTagsPresenterModel[] };
  presentedTagGroups: TagGroupPresenterModel[];
}

@State<CoreQueryStateModel>({
  name: 'coreQueries',
  defaults: {
    presentedQuery: {},
    presentedTags: {},
    presentedTagGroups: [],
  },
})
@Injectable()
export class CoreQueryState {
  constructor(
    private queriesService: DataQueryService,
    private queryFactoryService: QueryFactoryService,
    private tagService: TagsControllerService,
  ) {}

  private getContextKey = CoreQueryState.getContextKey;

  private static getContextKey(entityId: string, contextId?: string): string {
    return `${entityId}-${contextId ?? ''}`;
  }
  static presentedQuery(
    queryId: string,
  ): (state: CoreQueryStateModel) => DataQueryPresenterModel {
    return createSelector(
      [CoreQueryState],
      (state: CoreQueryStateModel) => state.presentedQuery[queryId],
    );
  }

  static presentedTags(
    entityId: string,
    viewId?: string,
  ): (state: CoreQueryStateModel) => AllTagsPresenterModel[] {
    return createSelector(
      [CoreQueryState],
      (state: CoreQueryStateModel) =>
        state.presentedTags[this.getContextKey(entityId, viewId)],
    );
  }
  @Selector()
  static presentedTagGroups(state: CoreQueryStateModel) {
    return state.presentedTagGroups;
  }

  @Action(CoreQueryActions.FetchDataQuery)
  fetchDataQuery(
    ctx: StateContext<CoreQueryStateModel>,
    { entity, id, cached }: CoreQueryActions.FetchDataQuery,
  ) {
    const _query = ctx.getState().presentedQuery[id];
    if (cached && !!_query) {
      return of(_query);
    }

    return this.queriesService
      .findById({
        entity,
        id,
      })
      .pipe(
        tap((res) => {
          ctx.setState(
            patch<CoreQueryStateModel>({
              presentedQuery: {
                [res.id]: this.queryFactoryService.createPresenterModel(res),
              },
            }),
          );
        }),
      );
  }

  @Action(CoreQueryActions.UpdateDataQuery)
  updateDataQuery(
    ctx: StateContext<CoreQueryStateModel>,
    { payload }: CoreQueryActions.UpdateDataQuery,
  ) {
    ctx.setState(
      patch<CoreQueryStateModel>({
        presentedQuery: {
          [payload.id]: payload,
        },
      }),
    );
  }

  @Action(CoreQueryActions.DataQueryRemoveSucess)
  deleteDataQuery(
    ctx: StateContext<CoreQueryStateModel>,
    { payload: { queryId } }: CoreQueryActions.DataQueryRemoveSucess,
  ) {
    ctx.setState(
      patch<CoreQueryStateModel>({
        presentedQuery: {
          [queryId]: null,
        },
      }),
    );
  }

  @Action(CoreQueryActions.FetchAllTags)
  fetchAllTags(
    ctx: StateContext<CoreQueryStateModel>,
    { payload }: CoreQueryActions.FetchAllTags,
  ) {
    const contextKey = this.getContextKey(payload.entityId, payload.viewId);
    ctx.dispatch(new LoadingActions.SetLoading(true));
    ctx.setState(
      patch<CoreQueryStateModel>({
        presentedTags: patch({ [contextKey]: [] }),
      }),
    );

    return this.tagService
      .getTags({
        entityId: payload.entityId,
        context: payload.viewId
          ? new ManagedHttpContext({
              publicAccessId: payload.viewId,
            })
          : null,
      })
      .pipe(
        tap((res) => {
          ctx.setState(
            patch<CoreQueryStateModel>({
              presentedTags: patch({ [contextKey]: res }),
            }),
          );
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetLoading(false));
        }),
      );
  }

  @Action(CoreQueryActions.FetchAllTagGroups, { cancelUncompleted: true })
  fetchAllTagGroups(
    ctx: StateContext<CoreQueryStateModel>,
    { payload }: CoreQueryActions.FetchAllTagGroups,
  ) {
    ctx.dispatch(new LoadingActions.SetLoading(true));

    ctx.patchState({
      presentedTagGroups: [],
    });
    return this.tagService
      .getTagGroups({
        entityId: payload.entityId,
      })
      .pipe(
        tap((res) => {
          ctx.patchState({
            presentedTagGroups: res,
          });
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetLoading(false));
        }),
      );
  }
}
